import React, { useCallback, useMemo } from 'react';

import {
  Edit,
  TextField,
  DateField,
  usePermissions,
  useNotify,
  Labeled,
  TabbedShowLayout,
  ReferenceManyField,
  Datagrid,
  Pagination,
  SelectField,
  useRecordContext,
  useDataProvider,
  useGetRecordId,
} from 'react-admin';

import {
  Card,
  CardContent,
  CardHeader,
  Divider,
  Grid,
} from '@mui/material';

import hasAccess from '../../utilities/hasAccess';
import errorNotification from '../../utilities/errorNotification';

import actions, { Actions } from '../../constants/actions';
import resourceNames from '../../constants/resourceNames';
import resourceTypes from '../../constants/resourceTypes';
import { boldDataGridStyle } from '../../constants/style/datagridStyles';

import EditArrayInput from '../../customFields/EditArrayInput';
import EditField from '../../customFields/EditField';
import EditImageField from '../../customFields/EditImageField';
import EditReferenceField from '../../customFields/EditReferenceField';
import ReferenceMemberField from '../../customFields/ReferenceMemberField';
import FormBreadcrumb from './FormBreadcrumb';
import { languagesChoices } from '../../constants/languages';

const WelcomePageSettings = ({ showEdit }: { showEdit: boolean }) => {
  const record = useRecordContext();

  if (!record?.skipWelcomePage) return null;

  return (
    <>
      <Grid size={12}>
        <EditField source="welcomeTitle" label="Welcome title" showEdit={showEdit} type="textarea" />
      </Grid>
      <Grid size={12}>
        <EditField source="welcomeMessage" label="Welcome message" showEdit={showEdit} type="textarea" />
      </Grid>
    </>
  );
};

const RequestPermissionSettings = ({ showEdit }: { showEdit: boolean }) => {
  const record = useRecordContext();
  if (!record?.requestSubmission?.shouldSubmitRequest) return null;

  return (
    <Grid size={12}>
      <EditReferenceField
        source="requestSubmission.process"
        label="Request process"
        showEdit={showEdit}
        reference={resourceNames.PROCESSES}
      />
    </Grid>
  );
};

const FormEdit = () => {
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const { permissions } = usePermissions<Actions[]>();
  const showEdit = useMemo(() => hasAccess(permissions, actions.FORM_UPDATE), [permissions]);

  const formId = useGetRecordId();

  const getLogoUrl = useCallback(async () => {
    const { data } = await dataProvider.getOne(`${resourceNames.FORMS}/${formId}`, { id: 'logo' });
    return data.downloadUrl as string;
  }, [dataProvider, formId]);

  return (
    <Edit
      mutationMode="pessimistic"
      redirect={false}
      mutationOptions={{
        onError: (e: unknown) => notify(errorNotification(e), { type: 'error' }),
      }}
    >
      <FormBreadcrumb />
      <TabbedShowLayout>
        <TabbedShowLayout.Tab label="Information">
          <Labeled label={false} sx={{ display: 'flex' }}>
            <Grid container spacing={6}>
              {/* Form details card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Form details" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Labeled label="ID">
                          <TextField source="id" />
                        </Labeled>
                      </Grid>
                      <Grid size={12}>
                        <Labeled label="Created at">
                          <DateField source="createdAt" showTime />
                        </Labeled>
                      </Grid>
                      <Grid size={12}>
                        <EditField source="title" label="Title" showEdit={showEdit} />
                      </Grid>
                      <Grid size={12}>
                        <EditImageField
                          source="logo"
                          label="Logo"
                          showEdit={showEdit}
                          getPreviewUrl={getLogoUrl}
                          emptyText="No logo"
                        />
                      </Grid>
                      <Grid size={12}>
                        <Labeled label="Language">
                          <SelectField source="language" choices={languagesChoices} />
                        </Labeled>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Integration configuration card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Integration configuration" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EditArrayInput
                          source="whitelistedRedirectUrls"
                          label="Whitelisted redirect URLs"
                          showEdit={showEdit}
                        />
                      </Grid>
                      <Grid size={12}>
                        <EditArrayInput
                          source="allowedIframeParentOrigins"
                          label="Allowed iframe parent origins"
                          showEdit={showEdit}
                        />
                      </Grid>
                      <Grid size={12}>
                        <EditField
                          source="requestSubmission.shouldSubmitRequest"
                          label="Should submit request"
                          showEdit={showEdit}
                          type="boolean"
                        />
                      </Grid>
                      <RequestPermissionSettings showEdit={showEdit} />
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Welcome page configuration card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Welcome page configuration" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EditField
                          source="skipWelcomePage"
                          label="Skip welcome page"
                          showEdit={showEdit}
                          type="boolean"
                        />
                      </Grid>
                      <WelcomePageSettings showEdit={showEdit} />
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>

              {/* Thank you page configuration card */}
              <Grid size={{ xs: 12, lg: 6 }}>
                <Card variant="outlined">
                  <CardHeader title="Thank you page configuration" />
                  <Divider />
                  <CardContent>
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <EditField source="thankYouTitle" label="Thank you title" showEdit={showEdit} type="textarea" />
                      </Grid>
                      <Grid size={12}>
                        <EditField source="thankYouMessage" label="Thank you message" showEdit={showEdit} type="textarea" />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Labeled>
        </TabbedShowLayout.Tab>
        {hasAccess(permissions, actions.AUDIT_TRACK_LIST) && (
          <TabbedShowLayout.Tab label="Audit trail" path="audit-tracks">
            <Grid container>
              <Grid size={12}>
                <Card variant="outlined">
                  <ReferenceManyField
                    reference="audit-tracks"
                    target="resource.id"
                    filter={{ 'resource.type': resourceTypes.FORM }}
                    sort={{ field: 'date', order: 'DESC' }}
                    pagination={<Pagination />}
                  >
                    <Datagrid sx={boldDataGridStyle} bulkActionButtons={false}>
                      <TextField sortable={false} source="id" label="ID" />
                      <ReferenceMemberField label="User" />
                      <TextField sortable={false} source="description" />
                      <DateField sortable source="date" showTime />
                    </Datagrid>
                  </ReferenceManyField>
                </Card>
              </Grid>
            </Grid>
          </TabbedShowLayout.Tab>
        )}
      </TabbedShowLayout>
    </Edit>
  );
};

export default FormEdit;
